
import { getCurrencyRate } from "@/api/catalogue";
import { searchWebmasters } from "@/api/org-cabinets-backend";
import { getOneEmployeeMethod, getOnePayment, patchPayment } from "@/api/payout";
import { IEmployeePayment } from "@/api/types/payout";
import SvgReset from "@/assets/icons/reload.svg";
import BaseDateTimePicker from "@/components-UI/BaseDateTimePicker.vue";
import BaseCard from "@/components/base/BaseCard.vue";
import Status from "@/components/base/Status.vue";
import ViewDateTime from "@/components/base/ViewDateTime.vue";
import EditBtn from "@/components/base/buttons/EditBtn.vue";
import BaseAutocomplete from "@/components/base/design/BaseAutocomplete.vue";
import EditModeTextField from "@/components/base/form/EditModeTextField.vue";
import paymentCurrencies from "@/mappings/paymentCurrencies";
import { USER_ROLES } from "@/mappings/user-roles";
import { showNotification, showServerError } from "@/utils";
import { getCurrentDate, getFormatDatesForDatepicker } from "@/utils/formatDates";
import { translatePaymentsStatusConfirmation, translatePaymentType } from "@/utils/translate";
import { Component, PropSync, Ref, Vue } from "vue-property-decorator";

@Component({
    components: {
        BaseDateTimePicker,
        EditBtn,
        EditModeTextField,
        BaseAutocomplete,
        ViewDateTime,
        BaseCard,
        Status,
        SvgReset,
    },
})
export default class PaymentDetail extends Vue {
    @PropSync("payment", { required: true }) paymentSync!: IEmployeePayment;
    @PropSync("user", { required: true }) userSync!: any;
    @Ref("baseCardRef") baseCardRef!: BaseCard;

    isEditMode: boolean = false;
    sumValue: number = 0;
    periodValue: string | null = null;
    commentValue: string = "";
    commissionValue: number = 0;
    rateValue: number = 0;
    currencyValue: string = "";
    earlyPaymentID: string = "";
    rateOperation: "division" | "multiplication" = "multiplication";
    currencies = paymentCurrencies;
    requisite: string = "";
    roles: string[] = [USER_ROLES.ADMIN, USER_ROLES.SUPERVISOR, USER_ROLES.FINANCIER];

    searchWebmasters = searchWebmasters;

    async created(): Promise<void> {
        this.earlyPaymentID = this.$route.params.id;
        await this.getPayment();

        const isAffiliateCreated = !this.paymentSync.by_manager;
        const isStatusNewOrRejected = this.paymentSync.status === "new" || this.paymentSync.status === "rejected";
        const isApproved = this.paymentSync.status_confirmation === "approved";

        if (isStatusNewOrRejected && isAffiliateCreated && !isApproved && this.isEditable) {
            this.isEditMode = true;
            this.baseCardRef.isEditMode = true;
        }
    }

    get isEditable(): boolean {
        // Проверка на роли ADMIN, SUPERVISOR, FINANCIER
        if (this.userSync.roles.some((role) => this.roles.includes(role))) {
            return true;
        }

        // Проверка на ROLE_SUPPORT - если есть, сразу возвращаем false
        if (this.userSync.roles.includes("ROLE_SUPPORT")) {
            return false;
        }

        // Проверка для ROLE_AFFILIATE_MANAGER
        const isAffiliateManager = this.userSync.roles.includes("ROLE_AFFILIATE_MANAGER");
        const isNotDone = this.paymentSync.status !== "done";
        const isCreator = this.userSync.account_id === this.paymentSync.affiliate?.manager?.id;

        if (isAffiliateManager && isNotDone && isCreator) {
            return true;
        }

        const isAffiliateCreated = !this.paymentSync.by_manager;
        const isStatusNewOrRejected = this.paymentSync.status === "new" || this.paymentSync.status === "rejected";

        // Если создано партнёром и статус new/rejected - можно редактировать
        if (!isAffiliateManager && isAffiliateCreated && isStatusNewOrRejected) {
            return true;
        }

        return false;
    }

    get getCurrencyItems(): any {
        return this.currencies.map((i) => {
            return { itemText: i, itemValue: i };
        });
    }

    get isDisableSave(): boolean {
        return this.isEditMode && this.isNotValidForm;
    }

    get isNotValidForm(): boolean {
        return (
            !this.sumValue ||
            (this.isDisableCommission ? false : !(this.commissionValue === 0 || this.commissionValue)) ||
            !this.currencyValue ||
            !(this.rateValue === 0 || this.rateValue) ||
            !this.requisite
        );
    }

    valuesWithCommission = {
        tether_v1: 0,
        bank_card_ru_v1: 8.5,
        bank_card_ua_v1: 8.5,
        capitalist_v1: 5,
        capitalist_kz_v1: 5,
    };

    get isDisableCommission(): boolean {
        const method = this.paymentSync.method_id!;
        return !(method in this.valuesWithCommission);
    }

    get isDisableRate(): boolean {
        const { currency } = this.paymentSync;
        return !currency || !this.currencyValue || currency === this.currencyValue;
    }

    get isContract(): boolean {
        return this.paymentSync.method_id === "contract_v1";
    }

    get getManagerName(): string {
        const { affiliate } = this.paymentSync;
        return `${affiliate?.manager?.id} ${affiliate?.manager?.name}`;
    }

    get getPaidSumValue(): string {
        return `${(this.paymentSync.paid_sum! || 0).toLocaleString("ru-RU", {
            maximumFractionDigits: 2,
            minimumFractionDigits: 2,
        })} ${this.paymentSync.currency!}`;
    }

    get calculateCommission(): string {
        return `${(this.sumValue * (this.commissionValue / 100)).toLocaleString("ru-RU", {
            maximumFractionDigits: 2,
            minimumFractionDigits: 2,
        })} ${this.paymentSync.currency!}`;
    }

    get getCommissionValue(): string {
        if (!this.paymentSync.commission) return "Не указана";

        const percent = `${(this.paymentSync.commission! * 100).toFixed(2)}%`;
        const money = `${(this.paymentSync.paid_sum! * this.paymentSync.commission!).toLocaleString("ru-RU", {
            maximumFractionDigits: 2,
            minimumFractionDigits: 2,
        })} ${this.paymentSync.currency!}`;

        return `${percent} ${money}`;
    }

    get getRateValue(): string {
        if (!this.paymentSync.exchange_rates?.rate) return "Не указан";

        return `${(this.paymentSync.exchange_rates?.rate! || 0).toLocaleString("ru-RU", {
            maximumFractionDigits: 2,
            minimumFractionDigits: 2,
        })}`;
    }

    get isShowTotalPayout(): boolean {
        if (this.isEditMode) {
            return !!(this.commissionValue === 0 || this.commissionValue) && !!(this.rateValue === 0 || this.rateValue) && !!this.currencyValue;
        } else {
            return true;
        }
    }

    get getTotalPayout(): string {
        if (!this.isEditMode) {
            return `${this.getFormattedNumber(this.paymentSync.final_payout!)} ${this.getPayoutCurrency}`;
        }

        const raw = this.getTotalPayoutNumber;
        if (this.isDisableRate) {
            return `${this.getFormattedNumber(raw)} ${this.getPayoutCurrency}`;
        }

        const res = this.getTotalPayoutNumberWithRate;
        return `${this.getFormattedNumber(res)} ${this.currencyValue}`;
    }

    getFormattedNumber(value: number): string {
        return value ? value.toLocaleString("ru-RU", {
            maximumFractionDigits: 2,
            minimumFractionDigits: 2,
        }) : "0,00";
    }

    get getTotalPayoutNumber(): number {
        const sum = this.sumValue!;
        const com = sum * (this.commissionValue / 100);
        return parseFloat((sum - com).toFixed(2));
    }

    get getTotalPayoutNumberWithRate(): number {
        const raw = this.getTotalPayoutNumber;
        const rate = this.rateValue;
        const isMulti = this.rateOperation === "multiplication";
        return parseFloat((isMulti ? raw * rate : raw / rate).toFixed(2));
    }

    get getDataForCard(): any[] {
        return [
            {
                route: "affiliate",
                param: this.paymentSync.affiliate?.account_id,
                text: "Партнёр",
                value: `${this.paymentSync.affiliate?.account_id} ${this.paymentSync.affiliate?.name}`,
            },
            {
                text: "Кем создано",
                value: this.paymentSync.by_manager ? "Менеджер" : "Партнёр",
            },
            {
                isStatus: true,
                text: "Статус подтверждения",
                value: this.paymentSync.status_confirmation!,
                translate: translatePaymentsStatusConfirmation,
            },
            {
                text: "Менеджер",
                value: this.getManagerName,
            },
            {
                text: "Тип выплаты",
                value: translatePaymentType(this.paymentSync.payment_type!),
            },
            {
                isChip: true,
                route: "affiliate-invoice",
                param: this.paymentSync.invoice_id,
                text: "ID счёта",
                value: this.paymentSync.invoice_id || "",
            },
            {
                editType: "period",
                text: "Период",
                value: getFormatDatesForDatepicker(this.paymentSync.period!, "date"),
            },
            {
                text: "Код",
                value: this.paymentSync.code || "",
            },
            {
                editType: "sum",
                text: "Сумма списания",
                value: this.getPaidSumValue,
                isRequired: true,
                isError: !this.sumValue,
            },
            {
                isDate: true,
                text: "Дата создания",
                value: this.paymentSync.created_at,
            },
            {
                editType: "commission",
                text: "Комиссия",
                value: this.getCommissionValue,
                isRequired: true,
                isError: this.isDisableCommission ? false : !(this.commissionValue === 0 || this.commissionValue),
            },
            {
                text: "Способ выплаты",
                value: this.paymentSync.method_name ?? "",
            },
            {
                editType: "payout_currency",
                text: "Валюта выплаты",
                value: this.getPayoutCurrency,
                isRequired: true,
                isError: !this.currencyValue,
            },
            {
                editType: "requisite",
                text: "Реквизиты",
                value: this.requisite,
            },
            {
                editType: "rate",
                text: "Курс",
                value: this.getRateValue,
                isRequired: true,
                isError: !(this.rateValue === 0 || this.rateValue),
            },
            {
                editType: "comment",
                text: "Комментарий",
                value: this.paymentSync.comment || "",
            },
        ];
    }

    get getPayoutCurrency(): string {
        const { final_currency, exchange_rates, currency } = this.paymentSync;
        return exchange_rates?.currency! ?? (final_currency! || currency || "");
    }

    get isNotChanged(): boolean {
        const cardData = {
            requisite: this.requisite,
            sum: +this.sumValue,
            period: this.periodValue,
            comment: this.commentValue,
            commission: this.commissionValue,
            currency: this.currencyValue,
            rate: this.rateValue,
        };
        const paymentData = {
            requisite: this.paymentSync.requisite,
            sum: this.paymentSync.paid_sum,
            period: this.paymentSync.period,
            comment: this.paymentSync.comment,
            commission: this.paymentSync.commission,
            currency: this.paymentSync.exchange_rates?.currency,
            rate: this.paymentSync.exchange_rates?.rate,
        };
        return JSON.stringify(cardData) === JSON.stringify(paymentData);
    }

    checkRules(value: any, name: string): boolean | string {
        if (name === "card_number") {
            return value.replace(/\d{16,18}/g, "").length > 0 ? "" : true;
        }
        if (name === "account_id" && this.paymentSync.method_id === "qiwi_v1") {
            return value.replace(/7\d{10}/g, "").length > 0 ? "" : true;
        }
        if (name === "account_id" && this.paymentSync.method_id === "webmoney_v1") {
            return value.replace(/Z\d{12}/g, "").length > 0 ? "" : true;
        }
        if (name === "account_id" && this.paymentSync.method_id === "yandex_v1") {
            return value.replace(/\w{15}/g, "").length > 0 ? "" : true;
        }
        return !!value || "";
    }

    async getPayment(): Promise<void> {
        try {
            const { data } = await getOnePayment(this.earlyPaymentID);
            this.paymentSync = data;
            this.requisite = data.requisite!;
            this.sumValue = data.paid_sum!;
            this.periodValue = data.period!;
            this.commentValue = data.comment!;
            this.commissionValue = (data.commission! || 0) * 100;
            this.currencyValue = data.exchange_rates?.currency!;
            this.rateValue = data.exchange_rates?.rate! || 0;
        } catch (err) {
            showServerError(err, "Досрочка не загружена");
        }
    }

    async getRateData(cur: string, exCur: string, isUpdateCurr: boolean = false): Promise<void> {
        if (!cur || !exCur || cur === exCur) return;
        try {
            const {
                original_rate,
                operation,
            }: {
                original_rate: number;
                operation: "division" | "multiplication";
            } = await getCurrencyRate({
                source: "excel",
                currency_from: cur,
                currency_to: exCur,
                date: getCurrentDate("yyyy-MM-dd"),
            });
            if (isUpdateCurr) {
                this.rateValue = original_rate;
            } else {
                this.rateValue = this.paymentSync.exchange_rates?.rate! || 0;
            }
            this.rateOperation = operation;
        } catch (err) {
            if (err.status === 404) {
                this.rateOperation = "multiplication";
            } else {
                showServerError(err, "Данные курса не загружены");
            }
        }
    }

    setCurrency(currency: string): void {
        if (this.currencyValue === currency) {
            this.rateValue = 0;
            return;
        }
        this.currencyValue = currency;
        this.getRateData(this.paymentSync.currency!, currency, true);
    }

    get isExRatesChanged(): boolean {
        const { exchange_rates } = this.paymentSync;
        return exchange_rates?.rate! !== this.rateValue || exchange_rates?.currency! !== this.currencyValue;
    }

    get isCurEqual(): boolean {
        return this.paymentSync.currency === this.currencyValue;
    }

    async updatePayment(event: boolean): Promise<void> {
        this.isEditMode = event;
        if (!event) {
            if (this.isNotChanged) return;
            try {
                const exchange_rates = this.isCurEqual ? [] :
                    {
                        currency: this.currencyValue,
                        rate: this.rateValue,
                        date: getCurrentDate("yyyy-MM-dd"),
                    };

                const method = await getOneEmployeeMethod(this.paymentSync.method_id!);
                const { partner_fields_required, partner_fields_keys, manager_fields_keys } = method;
                const managerData = { [manager_fields_keys[0]]: this.requisite };
                const partnerData = { [partner_fields_keys[0]]: this.requisite };
                const requisite = {
                    manager_data: partner_fields_required ? {} : managerData,
                    partner_data: partner_fields_required ? partnerData : {},
                };

                await patchPayment(this.paymentSync.id!, {
                    paid_sum: Number(this.sumValue),
                    final_payout: this.isDisableRate ? this.getTotalPayoutNumber : this.getTotalPayoutNumberWithRate,
                    period: this.periodValue,
                    comment: this.commentValue,
                    commission: this.commissionValue ? parseFloat((this.commissionValue / 100).toFixed(4)) : 0,
                    requisite,
                    exchange_rates,
                });
                showNotification("Изменения сохранены");
                if (!this.paymentSync.by_manager && this.paymentSync.status_confirmation !== "approved") {
                    this.$emit("change-status");
                } else {
                    await this.getPayment();
                }
            } catch (err) {
                showServerError(err, "Ошибка сохранения");
            }
        } else {
            this.getRateData(this.paymentSync.currency!, this.currencyValue);
        }
    }

    resetValues(): void {
        this.periodValue = this.paymentSync.period!;
        this.sumValue = this.paymentSync.paid_sum!;
        this.commentValue = this.paymentSync.comment!;
        this.commissionValue = (this.paymentSync.commission! || 0) * 100;
        this.currencyValue = this.paymentSync.exchange_rates?.currency!;
        this.rateValue = this.paymentSync.exchange_rates?.rate! || 0;
        this.requisite = this.paymentSync.requisite!;
        this.isEditMode = false;
    }
}
